import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoadingOverlayService } from '../../services/loading-overlay.service';
import { TranslationService } from '../../services/translation.service';
import { TimingMetricsService } from '../../services/timing-metrics.service';
import { ElapsedTimerComponent } from '../shared/elapsed-timer/elapsed-timer.component';
import type { LoadingOverlayTranslations } from '../../i18n/loading-overlay';
import enTranslations from '../../i18n/en/loading-overlay';

@Component({
  selector: 'app-global-loading-overlay',
  standalone: true,
  imports: [CommonModule, ElapsedTimerComponent],
  template: `
    @if (overlay.state$ | async; as state) {
      @if (state.visible) {
        <!-- Shared full-screen backdrop + animated glow + bottom-anchored
             panel position (unchanged) - only the panel's own inner
             content is swapped between the two designs below. -->
        <div class="cv-optimization-overlay">
          <div class="cv-optimization-overlay__glow"></div>
          <div class="cv-optimization-overlay__glow cv-optimization-overlay__glow--secondary"></div>

          <div class="cv-optimization-overlay__panel">
            @if (state.progressLog) {
              <!-- Timeline-card design (chamba-ai#395) - only for the two
                   "tailoring" call sites (trackProgress+cancellable);
                   every other overlay caller keeps the design below. -->
              <div class="cv-tailoring-overlay__card">
                <div class="cv-tailoring-overlay__header">
                  <h2 class="cv-tailoring-overlay__title">{{ state.message || t.WORKING }}</h2>
                  <div class="cv-tailoring-overlay__header-actions">
                    <button
                      type="button"
                      class="cv-tailoring-overlay__btn cv-tailoring-overlay__btn--text"
                      (click)="expanded = !expanded"
                    >
                      {{ expanded ? t.TIMELINE_HIDE : t.TIMELINE_SHOW }}
                    </button>
                    @if (state.cancellable && !state.cancelConfirming) {
                      <button
                        type="button"
                        class="cv-tailoring-overlay__btn cv-tailoring-overlay__btn--cancel"
                        [disabled]="!!state.cancelling"
                        (click)="overlay.requestCancelConfirmation()"
                      >
                        {{ t.CANCEL_BUTTON }}
                      </button>
                    }
                  </div>
                </div>

                @if (state.cancelConfirming) {
                  <div class="cv-tailoring-overlay__confirm">
                    <span>{{ t.CANCEL_CONFIRM_MESSAGE }}</span>
                    <div class="cv-tailoring-overlay__confirm-actions">
                      <button
                        type="button"
                        class="cv-tailoring-overlay__btn cv-tailoring-overlay__btn--text"
                        (click)="overlay.dismissCancelConfirmation()"
                      >
                        {{ t.CANCEL_CONFIRM_NO }}
                      </button>
                      <button
                        type="button"
                        class="cv-tailoring-overlay__btn cv-tailoring-overlay__btn--danger"
                        (click)="overlay.confirmCancel(t.CANCELLING_MESSAGE)"
                      >
                        {{ t.CANCEL_CONFIRM_YES }}
                      </button>
                    </div>
                  </div>
                }

                @if (expanded) {
                  <div class="cv-tailoring-overlay__body">
                    @if (state.afterStats && state.showStats) {
                      <div class="cv-tailoring-overlay__stats">
                        {{ t.OPTIMIZED_FROM }} <span class="cv-tailoring-overlay__highlight">{{ state.beforeStats?.coveragePercent || 0 }}%</span>
                        {{ t.TO }} <span class="cv-tailoring-overlay__highlight cv-tailoring-overlay__highlight--success">{{ state.afterStats.coveragePercent || 0 }}%</span> {{ t.COVERAGE }}
                        · <span class="cv-tailoring-overlay__highlight cv-tailoring-overlay__highlight--success">{{ state.afterStats.wordCount || 0 }}</span> {{ t.WORDS }}
                      </div>
                    }
                    @for (line of state.progressLog; track $index) {
                      <div class="cv-tailoring-step cv-tailoring-step--completed">
                        <span class="cv-tailoring-step__icon">✓</span>
                        <div class="cv-tailoring-step__title">{{ stripTrailingEllipsis(line.label) }}</div>
                        @if (timingMetricsService.isEnabled()) {
                          <div class="cv-tailoring-step__time">
                            {{ t.COMPLETED_IN_PREFIX }} <app-elapsed-timer [seconds]="line.durationSeconds"></app-elapsed-timer>
                          </div>
                        }
                      </div>
                    }
                    @if (!state.finished) {
                      <div class="cv-tailoring-step cv-tailoring-step--active">
                        <div class="cv-tailoring-step__title">
                          {{ t.TIMELINE_ACTIVE_STEP }}<span class="cv-tailoring-step__dots"></span>
                        </div>
                        @if (timingMetricsService.isEnabled()) {
                          <div class="cv-tailoring-step__time">
                            <app-elapsed-timer [startedAt]="state.lastStepAt ?? state.startedAt ?? null"></app-elapsed-timer> {{ t.ELAPSED_SUFFIX }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            } @else {
              <div class="cv-optimization-overlay__status-bar">
                <div class="cv-optimization-overlay__status-row">
                  <div class="cv-optimization-overlay__status-dot" [class.cv-optimization-overlay__status-dot--success]="state.afterStats"></div>
                  <span class="cv-optimization-overlay__status-text">
                    @if (state.afterStats && state.showStats) {
                      {{ t.OPTIMIZED_FROM }} <span class="cv-optimization-overlay__highlight">{{ state.beforeStats?.coveragePercent || 0 }}%</span>
                      {{ t.TO }} <span class="cv-optimization-overlay__highlight cv-optimization-overlay__highlight--success">{{ state.afterStats.coveragePercent || 0 }}%</span> {{ t.COVERAGE }}
                      · <span class="cv-optimization-overlay__highlight cv-optimization-overlay__highlight--success">{{ state.afterStats.wordCount || 0 }}</span> {{ t.WORDS }}
                    } @else {
                      {{ state.message || t.WORKING }}
                      @if (timingMetricsService.isEnabled() && state.startedAt && !state.afterStats) {
                        <app-elapsed-timer class="cv-optimization-overlay__timer" [startedAt]="state.startedAt"></app-elapsed-timer>
                      }
                    }
                  </span>
                </div>
                @if (state.cancellable && !state.cancelConfirming) {
                  <div class="cv-optimization-overlay__status-actions">
                    <button
                      type="button"
                      class="cv-optimization-overlay__cancel-button"
                      [disabled]="!!state.cancelling"
                      (click)="overlay.requestCancelConfirmation()"
                    >
                      {{ t.CANCEL_BUTTON }}
                    </button>
                  </div>
                }
              </div>

              @if (state.cancelConfirming) {
                <div class="cv-optimization-overlay__cancel-confirm">
                  <span>{{ t.CANCEL_CONFIRM_MESSAGE }}</span>
                  <div class="cv-optimization-overlay__cancel-confirm-actions">
                    <button
                      type="button"
                      class="cv-optimization-overlay__cancel-confirm-no"
                      (click)="overlay.dismissCancelConfirmation()"
                    >
                      {{ t.CANCEL_CONFIRM_NO }}
                    </button>
                    <button
                      type="button"
                      class="cv-optimization-overlay__cancel-confirm-yes"
                      (click)="overlay.confirmCancel(t.CANCELLING_MESSAGE)"
                    >
                      {{ t.CANCEL_CONFIRM_YES }}
                    </button>
                  </div>
                </div>
              }
            }
          </div>
        </div>
      }
    }
  `,
  styleUrls: ['./global-loading-overlay.component.css'],
})
export class GlobalLoadingOverlayComponent implements OnInit {
  t: LoadingOverlayTranslations = enTranslations;
  // Shown expanded by default (a user asked why they had to click "Show
  // progress" every run just to see what the pipeline was doing) - still
  // toggleable, in case someone wants to collapse it to a compact status
  // line for a run they don't care to watch step by step.
  expanded = true;

  constructor(
    public overlay: LoadingOverlayService,
    private translationService: TranslationService,
    protected timingMetricsService: TimingMetricsService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.translationService.onChange(() => this.loadTranslations());
    this.timingMetricsService.ensureLoaded();
    // Re-expand at the start of every new overlay run, in case a
    // previous run was manually collapsed - each run should default to
    // expanded, not inherit the last run's manual choice.
    this.overlay.state$.subscribe((state) => {
      if (state.visible && state.progressLog?.length === 0) {
        this.expanded = true;
      }
    });
  }

  private loadTranslations() {
    this.t = this.translationService.getLoadingOverlayTranslations() || enTranslations;
  }

  /** The shared PROGRESS label map (i18n/loading-overlay.ts) is written
   * for an "in progress" context ("Reading the job description...") and
   * always ends in an ellipsis - once a step is displayed as completed
   * in the timeline (chamba-ai#395), that trailing "..."/"…" reads oddly
   * next to "Completed in Xs", so it's stripped for display only; the
   * canonical translation strings themselves are unchanged. */
  stripTrailingEllipsis(label: string): string {
    return label.replace(/(\.\.\.|…)$/, '');
  }
}
